import React, { useEffect, useMemo, useState } from "react";
import {
  Loader2, Plus, Search, Mail, Phone, MapPin, X, Send, Check, AlertCircle,
  Pencil, Trash2, ChevronLeft, ShieldCheck, Calendar, DollarSign, Link as LinkIcon, Copy,
} from "lucide-react";
import { api } from "../../lib/api";

const STATUSES = ["scheduled", "in_progress", "completed", "cancelled"];

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-AU", {
      day: "numeric", month: "short", year: "numeric",
    });
  } catch {
    return "";
  }
}

function fmtMoney(n) {
  if (n == null || n === "") return "—";
  return new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(Number(n));
}

// =========================== ROOT PANEL =====================================

export default function CustomersPanel() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [toast, setToast] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/customers${search ? `?search=${encodeURIComponent(search)}` : ""}`);
      setCustomers(r.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showToast = (kind, msg) => {
    setToast({ kind, msg });
    setTimeout(() => setToast(null), 4000);
  };

  if (selected) {
    return (
      <CustomerDetail
        customer={selected}
        onBack={() => { setSelected(null); load(); }}
        onToast={showToast}
        onUpdated={(c) => setSelected(c)}
      />
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-customers-panel">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            type="text"
            placeholder="Search by name, email, phone, suburb…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            data-testid="customers-search-input"
            className="w-full bg-black/40 border border-white/10 pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[var(--te-primary)]"
          />
        </div>
        <button
          onClick={load}
          className="te-btn te-btn-ghost text-sm"
          data-testid="customers-refresh-btn"
        >
          Refresh
        </button>
        <button
          onClick={() => setShowCreate(true)}
          data-testid="customers-add-btn"
          className="te-btn te-btn-primary text-sm"
        >
          <Plus size={14} /> Add customer
        </button>
      </div>

      {/* Toast */}
      {toast && (
        <div
          className={`fixed bottom-6 right-6 z-50 te-card px-4 py-3 max-w-sm shadow-2xl flex items-start gap-2 ${
            toast.kind === "error" ? "border-red-500/40" : "border-white/20"
          }`}
          data-testid="customers-toast"
        >
          {toast.kind === "error" ? (
            <AlertCircle size={14} className="text-red-400 mt-0.5" />
          ) : (
            <Check size={14} style={{ color: "var(--te-primary)" }} className="mt-0.5" />
          )}
          <span className="text-sm">{toast.msg}</span>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="py-8 flex items-center gap-2 text-zinc-500 text-sm">
          <Loader2 size={14} className="animate-spin" /> Loading customers…
        </div>
      ) : customers.length === 0 ? (
        <div className="te-card p-10 text-center">
          <h3 className="text-lg font-bold tracking-tight mb-1">No customers yet.</h3>
          <p className="text-sm text-zinc-400">
            Add one to start tracking jobs in the portal.
          </p>
        </div>
      ) : (
        <ul className="space-y-2">
          {customers.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                onClick={() => setSelected(c)}
                data-testid={`customer-row-${c.id}`}
                className="te-card w-full text-left p-4 flex items-center gap-4 hover:border-white/30 transition"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-base font-semibold">{c.name}</div>
                    {c.activated_at ? (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] tracking-[0.15em] font-bold uppercase text-green-400 bg-green-500/10">
                        <ShieldCheck size={9} /> Activated
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] tracking-[0.15em] font-bold uppercase text-zinc-500 bg-white/5">
                        Invite pending
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-zinc-400 mt-1 flex flex-wrap items-center gap-3">
                    <span className="inline-flex items-center gap-1"><Mail size={10} /> {c.email}</span>
                    {c.phone && <span className="inline-flex items-center gap-1"><Phone size={10} /> {c.phone}</span>}
                    {c.suburb && <span className="inline-flex items-center gap-1"><MapPin size={10} /> {c.suburb}</span>}
                  </div>
                </div>
                <div className="text-[10px] text-zinc-500 hidden sm:block">
                  Added {fmtDate(c.created_at)}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      {showCreate && (
        <CustomerFormModal
          title="Add a customer"
          onClose={() => setShowCreate(false)}
          onSubmit={async (payload) => {
            try {
              await api.post("/admin/customers", payload);
              setShowCreate(false);
              showToast("ok", "Customer added.");
              load();
            } catch (e) {
              showToast("error", e?.response?.data?.detail || "Couldn't create customer.");
            }
          }}
        />
      )}
    </div>
  );
}

// =========================== CUSTOMER DETAIL ================================

function CustomerDetail({ customer, onBack, onToast, onUpdated }) {
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [showCreateJob, setShowCreateJob] = useState(false);

  const loadJobs = async () => {
    setLoadingJobs(true);
    try {
      const r = await api.get(`/admin/customers/${customer.id}/jobs`);
      setJobs(r.data || []);
    } finally {
      setLoadingJobs(false);
    }
  };

  useEffect(() => { loadJobs(); }, [customer.id]); // eslint-disable-line

  const [manualLink, setManualLink] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);

  const sendInvite = async () => {
    try {
      const { data } = await api.post(`/admin/customers/${customer.id}/invite`);
      if (data.email_sent) {
        onToast("ok", `Invite emailed to ${customer.email}.`);
        // Hide any prior manual-link block — they don't need it now.
        setManualLink(null);
      } else {
        // Resend silently failed (most often: unverified sender domain).
        // Reveal the link automatically as a fallback + copy to clipboard.
        setManualLink(data.link);
        try {
          await navigator.clipboard.writeText(data.link);
          setLinkCopied(true);
          setTimeout(() => setLinkCopied(false), 4000);
        } catch { /* clipboard can be blocked — link is still visible below */ }
        onToast(
          "error",
          (data.email_error || "Email couldn't be delivered.") +
            " Activation link copied to your clipboard — send it via SMS or WhatsApp.",
        );
      }
    } catch (e) {
      onToast("error", e?.response?.data?.detail || "Couldn't send invite.");
    }
  };

  const generateManualLink = async () => {
    try {
      const { data } = await api.post(`/admin/customers/${customer.id}/invite-link`);
      setManualLink(data.link);
      try {
        await navigator.clipboard.writeText(data.link);
        setLinkCopied(true);
        onToast("ok", "Activation link copied — paste it into SMS / WhatsApp / your email.");
        setTimeout(() => setLinkCopied(false), 4000);
      } catch {
        // Clipboard API can fail on non-https or older mobile browsers — link is still visible below.
        onToast("ok", "Activation link generated — tap to copy below.");
      }
    } catch (e) {
      onToast("error", e?.response?.data?.detail || "Couldn't generate link.");
    }
  };

  const copyManualLink = async () => {
    if (!manualLink) return;
    try {
      await navigator.clipboard.writeText(manualLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {}
  };

  return (
    <div className="space-y-5" data-testid="customer-detail">
      <button
        type="button"
        onClick={onBack}
        className="te-overline inline-flex items-center gap-2 hover:text-white text-zinc-400"
        data-testid="customer-detail-back"
      >
        <ChevronLeft size={14} /> All customers
      </button>

      {/* Customer card */}
      <div className="te-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">{customer.name}</h2>
            <div className="text-xs text-zinc-400 mt-2 flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center gap-1"><Mail size={11} /> {customer.email}</span>
              {customer.phone && <span className="inline-flex items-center gap-1"><Phone size={11} /> {customer.phone}</span>}
              {customer.suburb && <span className="inline-flex items-center gap-1"><MapPin size={11} /> {customer.suburb}</span>}
            </div>
            {customer.address && (
              <div className="text-xs text-zinc-500 mt-1">{customer.address}</div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowEdit(true)}
              className="te-btn te-btn-ghost text-xs"
              data-testid="customer-edit-btn"
            >
              <Pencil size={12} /> Edit
            </button>
            <button
              onClick={sendInvite}
              className="te-btn te-btn-primary text-xs"
              data-testid="customer-invite-btn"
            >
              <Send size={12} /> {customer.activated_at ? "Resend invite" : "Send invite"}
            </button>
            <button
              onClick={generateManualLink}
              className="te-btn te-btn-ghost text-xs"
              data-testid="customer-invite-link-btn"
              title="Generate an activation link to send manually via SMS / WhatsApp / your own email"
            >
              <LinkIcon size={12} /> Copy link
            </button>
          </div>
        </div>
        <div className="text-[10px] tracking-[0.18em] uppercase font-bold mt-1">
          {customer.activated_at ? (
            <span style={{ color: "#22c55e" }}>
              ✓ Activated · {fmtDate(customer.activated_at)}
            </span>
          ) : (
            <span className="text-zinc-500">Invite pending — they haven&apos;t set a password yet</span>
          )}
        </div>

        {manualLink && (
          <div
            className="mt-3 p-3 border border-white/10 bg-black/30 flex items-start gap-2"
            data-testid="customer-manual-invite-link"
          >
            <LinkIcon size={13} className="text-[var(--te-primary)] flex-shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-zinc-500 mb-1">
                Activation link · expires in 7 days
              </div>
              <div className="text-xs text-zinc-200 font-mono break-all leading-relaxed select-all">
                {manualLink}
              </div>
              <div className="text-[10px] text-zinc-500 mt-1.5">
                Paste this into SMS, WhatsApp, or your own email so {customer.name || "the customer"} can set their password.
              </div>
            </div>
            <button
              onClick={copyManualLink}
              className="te-btn te-btn-ghost text-xs flex-shrink-0"
              data-testid="customer-manual-link-copy"
            >
              {linkCopied ? <><Check size={12} className="text-green-400" /> Copied</> : <><Copy size={12} /> Copy</>}
            </button>
          </div>
        )}
      </div>

      {/* Jobs */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold tracking-tight">Jobs</h3>
        <button
          onClick={() => setShowCreateJob(true)}
          className="te-btn te-btn-primary text-xs"
          data-testid="customer-add-job-btn"
        >
          <Plus size={12} /> Add job
        </button>
      </div>

      {loadingJobs ? (
        <div className="py-4 flex items-center gap-2 text-zinc-500 text-sm">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      ) : jobs.length === 0 ? (
        <div className="te-card p-6 text-center text-sm text-zinc-400">
          No jobs yet for this customer.
        </div>
      ) : (
        <ul className="space-y-2">
          {jobs.map((j) => (
            <li key={j.id}>
              <button
                type="button"
                onClick={() => setEditingJob(j)}
                data-testid={`customer-job-${j.id}`}
                className="te-card w-full text-left p-4 flex items-center gap-4 hover:border-white/30 transition"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold mb-1">{j.service}</div>
                  <div className="text-xs text-zinc-400 flex flex-wrap items-center gap-2.5">
                    <span className="inline-flex items-center gap-1">
                      <Calendar size={10} /> {fmtDate(j.completed_at || j.created_at)}
                    </span>
                    <span
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] tracking-[0.15em] font-bold uppercase"
                      style={{
                        background:
                          j.status === "completed" ? "rgba(34,197,94,0.12)" :
                          j.status === "in_progress" ? "rgba(255,59,48,0.12)" :
                          j.status === "cancelled" ? "rgba(115,115,115,0.12)" :
                          "rgba(148,163,184,0.12)",
                        color:
                          j.status === "completed" ? "#22c55e" :
                          j.status === "in_progress" ? "#FF3B30" :
                          j.status === "cancelled" ? "#737373" : "#94a3b8",
                      }}
                    >
                      {j.status.replace("_", " ")}
                    </span>
                    {j.suburb && <span className="inline-flex items-center gap-1"><MapPin size={10} /> {j.suburb}</span>}
                  </div>
                </div>
                <div className="text-sm font-bold tabular-nums">
                  {fmtMoney(j.cost_total)}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Modals */}
      {showEdit && (
        <CustomerFormModal
          title="Edit customer"
          initial={customer}
          onClose={() => setShowEdit(false)}
          onSubmit={async (payload) => {
            try {
              const r = await api.patch(`/admin/customers/${customer.id}`, payload);
              setShowEdit(false);
              onToast("ok", "Customer updated.");
              onUpdated(r.data);
            } catch (e) {
              onToast("error", e?.response?.data?.detail || "Couldn't update.");
            }
          }}
        />
      )}
      {showCreateJob && (
        <JobFormModal
          title="Add job"
          onClose={() => setShowCreateJob(false)}
          onSubmit={async (payload) => {
            try {
              await api.post(`/admin/customers/${customer.id}/jobs`, payload);
              setShowCreateJob(false);
              onToast("ok", "Job added.");
              loadJobs();
            } catch (e) {
              onToast("error", e?.response?.data?.detail || "Couldn't add job.");
            }
          }}
        />
      )}
      {editingJob && (
        <JobFormModal
          title="Edit job"
          initial={editingJob}
          onClose={() => setEditingJob(null)}
          onDelete={async () => {
            if (!window.confirm("Delete this job permanently?")) return;
            try {
              await api.delete(`/admin/customers/${customer.id}/jobs/${editingJob.id}`);
              setEditingJob(null);
              onToast("ok", "Job deleted.");
              loadJobs();
            } catch (e) {
              onToast("error", e?.response?.data?.detail || "Couldn't delete.");
            }
          }}
          onSubmit={async (payload) => {
            try {
              await api.patch(`/admin/customers/${customer.id}/jobs/${editingJob.id}`, payload);
              setEditingJob(null);
              onToast("ok", "Job updated.");
              loadJobs();
            } catch (e) {
              onToast("error", e?.response?.data?.detail || "Couldn't update.");
            }
          }}
        />
      )}
    </div>
  );
}

// =========================== MODALS =========================================

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="te-card w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-zinc-950 border-b border-white/10 px-5 py-3 flex items-center justify-between">
          <h3 className="text-base font-bold tracking-tight">{title}</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="modal-close">
            <X size={18} />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function FormInput({ label, value, onChange, type = "text", required, testid, placeholder, autoFocus, fullWidth, rows }) {
  return (
    <label className={`block ${fullWidth ? "sm:col-span-2" : ""}`}>
      <span className="te-overline text-[10px] block mb-1.5">{label}{required && " *"}</span>
      {rows ? (
        <textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          autoFocus={autoFocus}
          rows={rows}
          placeholder={placeholder}
          data-testid={testid}
          className="w-full bg-black/40 border border-white/10 px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--te-primary)] resize-y"
        />
      ) : (
        <input
          type={type}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          autoFocus={autoFocus}
          placeholder={placeholder}
          data-testid={testid}
          className="w-full bg-black/40 border border-white/10 px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--te-primary)]"
        />
      )}
    </label>
  );
}

function CustomerFormModal({ title, initial = {}, onClose, onSubmit }) {
  const [form, setForm] = useState({
    name: initial.name || "",
    email: initial.email || "",
    phone: initial.phone || "",
    suburb: initial.suburb || "",
    address: initial.address || "",
  });
  const [submitting, setSubmitting] = useState(false);
  const isEdit = !!initial.id;

  const upd = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const payload = { ...form };
    if (isEdit) delete payload.email; // email is immutable on edit endpoint
    await onSubmit(payload);
    setSubmitting(false);
  };

  return (
    <Modal title={title} onClose={onClose}>
      <form onSubmit={submit} className="grid sm:grid-cols-2 gap-3" data-testid="customer-form">
        <FormInput label="Full name" required value={form.name} onChange={upd("name")} autoFocus testid="customer-form-name" />
        {!isEdit && (
          <FormInput label="Email" type="email" required value={form.email} onChange={upd("email")} testid="customer-form-email" />
        )}
        <FormInput label="Phone" value={form.phone} onChange={upd("phone")} testid="customer-form-phone" />
        <FormInput label="Suburb" value={form.suburb} onChange={upd("suburb")} testid="customer-form-suburb" />
        <FormInput label="Address (optional)" value={form.address} onChange={upd("address")} fullWidth testid="customer-form-address" />
        <div className="sm:col-span-2 flex items-center justify-end gap-2 pt-2 border-t border-white/10">
          <button type="button" onClick={onClose} className="te-btn te-btn-ghost text-sm">Cancel</button>
          <button type="submit" disabled={submitting} className="te-btn te-btn-primary text-sm" data-testid="customer-form-submit">
            {submitting ? <Loader2 size={14} className="animate-spin" /> : isEdit ? "Save" : "Add customer"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function JobFormModal({ title, initial = {}, onClose, onSubmit, onDelete }) {
  const [form, setForm] = useState({
    service: initial.service || "",
    suburb: initial.suburb || "",
    status: initial.status || "completed",
    cost_total: initial.cost_total ?? "",
    cost_breakdown: initial.cost_breakdown || [],
    customer_notes: initial.customer_notes || "",
    internal_notes: initial.internal_notes || "",
    completed_at: initial.completed_at ? initial.completed_at.slice(0, 10) : "",
  });
  const [submitting, setSubmitting] = useState(false);
  const upd = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const isEdit = !!initial.id;

  const updateItem = (i, field, value) => {
    setForm((f) => {
      const items = [...f.cost_breakdown];
      items[i] = { ...items[i], [field]: field === "amount" ? Number(value) || 0 : value };
      return { ...f, cost_breakdown: items };
    });
  };
  const addItem = () =>
    setForm((f) => ({ ...f, cost_breakdown: [...f.cost_breakdown, { label: "", amount: 0 }] }));
  const removeItem = (i) =>
    setForm((f) => ({ ...f, cost_breakdown: f.cost_breakdown.filter((_, idx) => idx !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const payload = {
      ...form,
      cost_total: form.cost_total === "" ? null : Number(form.cost_total),
      cost_breakdown: form.cost_breakdown.filter((li) => (li.label || "").trim().length > 0),
      completed_at: form.completed_at ? new Date(form.completed_at + "T00:00:00Z").toISOString() : null,
    };
    await onSubmit(payload);
    setSubmitting(false);
  };

  return (
    <Modal title={title} onClose={onClose}>
      <form onSubmit={submit} className="grid sm:grid-cols-2 gap-3" data-testid="job-form">
        <FormInput label="Service" required value={form.service} onChange={upd("service")} autoFocus testid="job-form-service" placeholder="e.g. Replaced HDD with SSD" />
        <label className="block">
          <span className="te-overline text-[10px] block mb-1.5">Status *</span>
          <select
            value={form.status}
            onChange={(e) => upd("status")(e.target.value)}
            data-testid="job-form-status"
            className="w-full bg-black/40 border border-white/10 px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--te-primary)]"
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>{s.replace("_", " ")}</option>
            ))}
          </select>
        </label>
        <FormInput label="Suburb" value={form.suburb} onChange={upd("suburb")} testid="job-form-suburb" />
        <FormInput label="Date completed" type="date" value={form.completed_at} onChange={upd("completed_at")} testid="job-form-completed-at" />

        <FormInput label="Cost total (AUD)" type="number" value={form.cost_total} onChange={upd("cost_total")} testid="job-form-total" />
        <div /> {/* spacer */}

        <div className="sm:col-span-2 te-card p-3 border-white/10">
          <div className="flex items-center justify-between mb-2">
            <div className="te-overline text-[10px]">/ Cost breakdown (line items)</div>
            <button type="button" onClick={addItem} className="text-xs text-zinc-400 hover:text-white inline-flex items-center gap-1" data-testid="job-form-add-line">
              <Plus size={11} /> Add line
            </button>
          </div>
          {form.cost_breakdown.length === 0 ? (
            <div className="text-xs text-zinc-500">No line items. Customer will see just the total.</div>
          ) : (
            <ul className="space-y-2">
              {form.cost_breakdown.map((li, i) => (
                <li key={i} className="grid grid-cols-[1fr_120px_auto] gap-2 items-center">
                  <input
                    type="text"
                    value={li.label}
                    onChange={(e) => updateItem(i, "label", e.target.value)}
                    placeholder="e.g. Labour (1.5h)"
                    data-testid={`job-form-line-label-${i}`}
                    className="bg-black/40 border border-white/10 px-2.5 py-1.5 text-xs focus:outline-none focus:border-[var(--te-primary)]"
                  />
                  <input
                    type="number"
                    value={li.amount}
                    onChange={(e) => updateItem(i, "amount", e.target.value)}
                    placeholder="0.00"
                    step="0.01"
                    data-testid={`job-form-line-amount-${i}`}
                    className="bg-black/40 border border-white/10 px-2.5 py-1.5 text-xs focus:outline-none focus:border-[var(--te-primary)]"
                  />
                  <button
                    type="button"
                    onClick={() => removeItem(i)}
                    className="text-zinc-500 hover:text-red-400 transition w-7 h-7 flex items-center justify-center"
                    data-testid={`job-form-line-remove-${i}`}
                  >
                    <X size={13} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <FormInput label="Your service notes (visible to customer)" fullWidth rows={4} value={form.customer_notes} onChange={upd("customer_notes")} placeholder="What we did. The customer will see this in their portal." testid="job-form-customer-notes" />
        <FormInput label="Internal notes (admin only — never shown to customer)" fullWidth rows={3} value={form.internal_notes} onChange={upd("internal_notes")} placeholder="Anything you want to remember for next time." testid="job-form-internal-notes" />

        <div className="sm:col-span-2 flex items-center justify-between gap-2 pt-2 border-t border-white/10">
          <div>
            {isEdit && onDelete && (
              <button type="button" onClick={onDelete} className="text-xs text-red-400 hover:text-red-300 inline-flex items-center gap-1" data-testid="job-form-delete">
                <Trash2 size={11} /> Delete
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} className="te-btn te-btn-ghost text-sm">Cancel</button>
            <button type="submit" disabled={submitting} className="te-btn te-btn-primary text-sm" data-testid="job-form-submit">
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <><DollarSign size={13} /> {isEdit ? "Save changes" : "Add job"}</>}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
}
